import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {ShopifyAPI} from "~/utils/api.ts";
import API_URLS from "~/utils/urls.ts";


interface ShopifyState {
    loading: boolean;
    error: any;
    products: any[];
    productImages: any[];
    theme: {};
}

const initialState: ShopifyState = {
    loading: false,
    error: null,
    productImages: [],
    products: [],
    theme: {},
};

export const fetchProducts = createAsyncThunk(
    'shopify/fetchProducts',
    async () => {
        try {
            const response = await ShopifyAPI.get(API_URLS.SHOPIFY.PRODUCTS);
            return response.data;
        } catch (error: any) {
            return error.response.data;
        }
    }
);

export const fetchThemeFiles = createAsyncThunk(
    'shopify/fetchThemeFiles',
    async () => {
        try {
            const response = await ShopifyAPI.get(API_URLS.SHOPIFY.THEME_FILES);
            return response.data;
        } catch (error: any) {
            return error.response.data;
        }
    }
);

export const updateThemeFile = createAsyncThunk(
    'shopify/updateThemeFile',
    async ({ formData, galleryId }: { formData: FormData, galleryId: string }) => {
        // Add gallery ID to form data
        formData.append('galleryId', galleryId);

        // Debug what's in the FormData before sending
        console.log('FormData contents before sending:');
        for (let pair of formData.entries()) {
            console.log(pair[0], pair[1]);
        }

        try {
            const response = await ShopifyAPI.post(
                API_URLS.SHOPIFY.THEME_FILES,
                formData,
                {
                    headers: {
                        // Don't set Content-Type when sending FormData
                        // The browser will automatically set it to multipart/form-data with the correct boundary
                        // 'Content-Type': 'multipart/form-data'
                    }
                }
            );
            return response.data;
        } catch (error: any) {
            console.error('Upload error:', error);
            return error.response?.data || { error: 'Network error' };
        }
    }
);

const appSlice = createSlice({
    name: 'shopify',
    initialState,
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProducts.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchProducts.fulfilled, (state, action) => {
                state.loading = false;
                state.productImages = action.payload;
            })
            .addCase(fetchProducts.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(fetchThemeFiles.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchThemeFiles.fulfilled, (state, action) => {
                state.loading = false;
                state.theme = action.payload;
            })
            .addCase(fetchThemeFiles.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { setLoading } = appSlice.actions;
export default appSlice.reducer;